import {
  useAuditDetail, useAuditReport, useScorecard, useActionPrioritisation, useSetScoreChart,
} from '../../../lib/api/queries';
import type { ReportSection, ScoreChartStyle } from '../../../lib/api/queries';
import { BandPill, BAND_COLORS } from '../ScorecardView';
import { ScoreChart, ScoreChartCard, ScoreChartTabs } from '../scoreCharts/ScoreCharts';
import { ActionTimelineChart } from './ActionTimelineChart';
import { EvidenceRegisterTable, type EvidenceRow } from './EvidenceTable';

/**
 * Read-only in-app render of a computed report section. Mirrors what the
 * PDF renderer will emit, so the auditor sees the section live before
 * exporting. Picks the body component from <see cref="ReportSection.computedKind"/>.
 */
export function ComputedSectionView({
  tenantId, auditId, section, evidenceRows = [], editable = false, scoreChart,
}: {
  tenantId: string;
  auditId: string;
  section: ReportSection;
  /** The report can be changed here (the chart chooser is enabled). */
  editable?: boolean;
  /**
   * Draw the compliance chart in this style, read-only. The previews pass
   * the style of the report or version being previewed; without it the
   * chart section shows the chooser.
   */
  scoreChart?: ScoreChartStyle;
  /**
   * Already-normalised evidence, passed down rather than fetched here so
   * the register and the per-section tables are guaranteed to be showing
   * the same set — and so this component stays free of a second data
   * source when it renders one of the other computed kinds.
   */
  evidenceRows?: EvidenceRow[];
}) {
  return (
    <div className="space-y-3">
      <header className="bg-gray-50 border border-gray-200 rounded-lg px-5 py-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Auto-computed — refreshes from the audit form
        </p>
        <h2 className="text-lg font-semibold text-gray-900 mt-0.5">{section.title}</h2>
      </header>
      <Body
        tenantId={tenantId}
        auditId={auditId}
        section={section}
        evidenceRows={evidenceRows}
        editable={editable}
        scoreChart={scoreChart}
      />
    </div>
  );
}

function Body({
  tenantId, auditId, section, evidenceRows, editable, scoreChart,
}: {
  tenantId: string;
  auditId: string;
  section: ReportSection;
  evidenceRows: EvidenceRow[];
  editable: boolean;
  scoreChart: ScoreChartStyle | undefined;
}) {
  switch (section.computedKind) {
    case 'cover':                return <CoverBody     tenantId={tenantId} auditId={auditId} />;
    case 'tableofcontents':      return <TableOfContentsBody tenantId={tenantId} auditId={auditId} />;
    case 'scopeareas':           return <ScopeAreasBody />;
    case 'scorecardtable':       return <ScorecardBody  tenantId={tenantId} auditId={auditId} />;
    case 'heatmap':
      return scoreChart
        ? <FixedScoreChartBody tenantId={tenantId} auditId={auditId} style={scoreChart} />
        : <ScoreChartChooserBody tenantId={tenantId} auditId={auditId} editable={editable} />;
    case 'actionprioritisation': return <PrioritisationBody tenantId={tenantId} auditId={auditId} />;
    case 'actiontimeline':       return <ActionTimelineChart tenantId={tenantId} auditId={auditId} />;
    case 'evidenceregister':     return <EvidenceRegisterTable rows={evidenceRows} />;
    default:
      return <p className="text-sm text-gray-500">No renderer for this section yet.</p>;
  }
}

function CoverBody({ tenantId, auditId }: { tenantId: string; auditId: string }) {
  const { data: audit } = useAuditDetail(tenantId, auditId);
  if (!audit) return null;
  const dateStr = audit.assessmentDate
    ? new Date(audit.assessmentDate).toLocaleDateString(undefined,
        { year: 'numeric', month: 'long' })
    : null;

  // Full-bleed title page: brand strip at the top + big centred title
  // block. Mirrors what the PDF cover page will look like once the
  // renderer is extended.
  return (
    <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
      <div className="h-2 flex">
        <div className="flex-1" style={{ backgroundColor: '#2BA84A' }} />
        <div className="flex-1" style={{ backgroundColor: '#2F7CB8' }} />
        <div className="flex-1" style={{ backgroundColor: '#8B3CA0' }} />
      </div>
      <div className="px-8 py-16 text-center space-y-6">
        <p className="text-xs font-semibold text-brand-700 uppercase tracking-[0.3em]">
          Data Privacy Advisory Service
        </p>
        <h1 className="text-4xl font-bold text-gray-900">Audit Report</h1>
        {audit.organisationName && (
          <p className="text-xl text-gray-800 font-semibold pt-4">
            {audit.organisationName}
          </p>
        )}
        <p className="text-sm text-gray-600 italic">{audit.title}</p>
        <div className="pt-8 space-y-1 text-sm text-gray-700">
          {audit.assessorName && <p>Prepared by <strong>{audit.assessorName}</strong></p>}
          {dateStr             && <p>{dateStr}</p>}
        </div>
      </div>
    </div>
  );
}

function TableOfContentsBody({
  tenantId, auditId,
}: { tenantId: string; auditId: string }) {
  const { data: report } = useAuditReport(tenantId, auditId);
  if (!report) return <p className="text-sm text-gray-500">Loading…</p>;

  // Hide the Cover (page-1 already) and the TOC itself from the list —
  // the reader shouldn't see either linked from within the table.
  const items = report.sections.filter(s =>
    s.computedKind !== 'cover' && s.computedKind !== 'tableofcontents');

  return (
    <div className="bg-white border border-gray-200 rounded-md p-6">
      <ol className="divide-y divide-gray-100">
        {items.map((s, i) => (
          <li key={s.id} className="py-2 flex items-baseline gap-3">
            <span className="text-xs text-gray-400 w-6 tabular-nums">{i + 1}.</span>
            <span className="flex-1 text-sm text-gray-800">{s.title}</span>
            <span className="text-[10px] text-gray-500 uppercase tracking-wide">
              {s.kind === 'Narrative' ? 'Narrative' : 'Auto-computed'}
            </span>
          </li>
        ))}
      </ol>
      <p className="text-[10px] text-gray-500 mt-3">
        Table of contents refreshes automatically as sections are added to
        the report catalog.
      </p>
    </div>
  );
}

function ScopeAreasBody() {
  // Static boilerplate — same paragraphs as the master Word template's
  // Scope and Purpose section. Kept hard-coded since the text is
  // identical across every audit; future template editor could move
  // them to data.
  return (
    <div className="bg-white border border-gray-200 rounded-md p-6 space-y-4 text-sm text-gray-800">
      {SCOPE_AREAS.map(a => (
        <div key={a.heading}>
          <h3 className="font-semibold text-gray-900">{a.heading}</h3>
          <p className="mt-1 leading-relaxed">{a.body}</p>
        </div>
      ))}
    </div>
  );
}

function ScorecardBody({ tenantId, auditId }: { tenantId: string; auditId: string }) {
  const { data } = useScorecard(tenantId, auditId);
  if (!data) return <p className="text-sm text-gray-500">Loading…</p>;
  return (
    <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-gray-700">Overall</span>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-900 tabular-nums">
            {Math.round(data.overall.percentage * 100)}%
          </span>
          <BandPill band={data.overall.band} />
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-2 text-left font-medium">Section</th>
            <th className="px-4 py-2 text-right font-medium">Score</th>
            <th className="px-4 py-2 text-right font-medium w-16">%</th>
            <th className="px-4 py-2 text-left font-medium w-40">Progress</th>
            <th className="px-4 py-2 text-left font-medium w-24">Band</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.sections.map(s => (
            <tr key={s.sectionId}>
              <td className="px-4 py-2 text-gray-800">{s.code} {s.name}</td>
              <td className="px-4 py-2 text-right text-gray-700 tabular-nums">
                {s.totalScore} / {s.potentialScore}
              </td>
              <td className="px-4 py-2 text-right text-gray-700 tabular-nums">
                {Math.round(s.percentage * 100)}%
              </td>
              <td className="px-4 py-2">
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full transition-all"
                       style={{
                         width: `${Math.max(0, Math.min(1, s.percentage)) * 100}%`,
                         backgroundColor: BAND_COLORS[s.band],
                       }} />
                </div>
              </td>
              <td className="px-4 py-2"><BandPill band={s.band} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The compliance-by-section figure on the Report tab, with tabs that set
 * the chart the report uses. The choice saves straight away; saving a
 * version freezes it, so a published report keeps its chart.
 */
function ScoreChartChooserBody({
  tenantId, auditId, editable,
}: { tenantId: string; auditId: string; editable: boolean }) {
  const { data } = useScorecard(tenantId, auditId);
  const { data: report } = useAuditReport(tenantId, auditId);
  const setChart = useSetScoreChart(tenantId, auditId);
  if (!data || !report) return <p className="text-sm text-gray-500">Loading…</p>;

  return (
    <ScoreChartCard
      title="Compliance by section"
      controls={
        <ScoreChartTabs value={report.scoreChart}
                        onChange={style => setChart.mutate(style)}
                        disabled={!editable || setChart.isPending}
                        label="Chart used in the report" />
      }
      note={
        setChart.isError
          ? <span className="text-danger-500">Couldn't change the chart. Try again.</span>
          : editable
            ? 'The selected chart is the one used in the report and its PDF. Saving a version locks in the chart for that version.'
            : 'The chart used in this report.'
      }>
      <ScoreChart style={report.scoreChart} sections={data.sections} />
    </ScoreChartCard>
  );
}

/** The figure in a given style, read-only: the previews of the report and of a saved version. */
function FixedScoreChartBody({
  tenantId, auditId, style,
}: { tenantId: string; auditId: string; style: ScoreChartStyle }) {
  const { data } = useScorecard(tenantId, auditId);
  if (!data) return <p className="text-sm text-gray-500">Loading…</p>;
  return (
    <ScoreChartCard title="Compliance by section">
      <ScoreChart style={style} sections={data.sections} />
    </ScoreChartCard>
  );
}

function PrioritisationBody({ tenantId, auditId }: { tenantId: string; auditId: string }) {
  const { data } = useActionPrioritisation(tenantId, auditId);
  if (!data) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!data.rows.length) {
    return (
      <p className="bg-success-50 border border-success-500/30 text-success-600
                    rounded-md px-4 py-3 text-sm">
        No items currently require remediation. Save a low score on the Form tab
        to see rows appear here automatically.
      </p>
    );
  }

  // Group by section for readability — matches the example PDF layout.
  const bySection = groupBy(data.rows, r => r.sectionId);
  return (
    <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-2 text-left font-medium w-12">Ref</th>
            <th className="px-4 py-2 text-left font-medium">Action</th>
            <th className="px-4 py-2 text-left font-medium w-24">Priority</th>
            <th className="px-4 py-2 text-right font-medium w-16">Score</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {[...bySection.entries()].map(([sectionId, rows]) => (
            <FragmentRow key={sectionId} sectionName={rows[0].sectionName} rows={rows} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Extract a single-row type from useActionPrioritisation's data shape via
// conditional-type inference. Historically this expression was written as
// the single-element type, but at the call site `rows` is an array — so
// we wrap it in Array<...> here to match how it's actually passed.
type ActionRow = ReturnType<typeof useActionPrioritisation>['data'] extends infer T
  ? T extends { rows: infer R } ? (R extends Array<infer U> ? U : never) : never
  : never;

function FragmentRow({
  sectionName, rows,
}: { sectionName: string; rows: ActionRow[] }) {
  // Render a sub-header row for the section then one row per item.
  const rowList = rows as unknown as Array<{
    refNumber: number; itemText: string; recommendedAction: string | null;
    priority: 'Low' | 'Medium' | 'High'; currentScore: number | null;
  }>;
  return (
    <>
      <tr className="bg-gray-50">
        <td colSpan={4} className="px-4 py-1.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
          {sectionName}
        </td>
      </tr>
      {rowList.map(r => (
        <tr key={r.refNumber}>
          <td className="px-4 py-2 text-gray-500 tabular-nums">{r.refNumber}</td>
          <td className="px-4 py-2 text-gray-800">
            <div>{r.recommendedAction || r.itemText}</div>
            {r.recommendedAction && (
              <div className="text-xs text-gray-500 mt-0.5">{r.itemText}</div>
            )}
          </td>
          <td className="px-4 py-2">
            <PriorityChip priority={r.priority} />
          </td>
          <td className="px-4 py-2 text-right text-gray-700 tabular-nums">
            {r.currentScore === -1 ? 'N/A' : r.currentScore ?? '—'}
          </td>
        </tr>
      ))}
    </>
  );
}

function PriorityChip({ priority }: { priority: 'Low' | 'Medium' | 'High' }) {
  // Same muted red-amber-green ramp as the Scorecard bands so the
  // Report tab's charts and chips read one coherent visual language.
  // High priority sits at the dusty-rose / red end; Medium the warm
  // sand; Low is neutral grey (no urgency).
  const colour =
    priority === 'High'   ? '#C97878' :   // dusty rose
    priority === 'Medium' ? '#E1B96B' :   // warm sand
                            '#9CA3AF';    // gray-400
  return (
    <span className="inline-flex text-[10px] font-semibold uppercase tracking-wide
                     px-2 py-0.5 rounded-full border"
          style={{
            color: colour,
            borderColor: `${colour}66`,      // ~40% alpha border
            backgroundColor: `${colour}1F`,  // ~12% alpha fill
          }}>
      {priority}
    </span>
  );
}

function groupBy<T, K>(items: T[], keyFn: (t: T) => K): Map<K, T[]> {
  const out = new Map<K, T[]>();
  for (const item of items) {
    const k = keyFn(item);
    const arr = out.get(k) ?? [];
    arr.push(item);
    out.set(k, arr);
  }
  return out;
}

// --- Static scope-area boilerplate (matches the master Word template) ---

const SCOPE_AREAS: { heading: string; body: string }[] = [
  { heading: 'Governance and Accountability',
    body: 'Compliance to the UK GDPR and other data protection legislation is demonstrated through (a) the policies and procedures that are in place, (b) demonstrable accountability regarding information governance and (c) controls and reporting mechanisms that support performance measurement. These should be enforced throughout the organisation with clear evidence trails.' },
  { heading: 'Training and Awareness',
    body: 'The audit is assessing the training of staff in the delivery and monitoring of data protection practices, records management, and information security. It looks to assess awareness levels throughout the organisation about the individual and collective responsibilities that each member of staff has with regards to data protection legislation.' },
  { heading: 'Records Management',
    body: 'The audit assesses the processes in place for managing records that contain personal data, including the controls and steps in place to oversee the creation, maintenance, storage, movement, retention, and destruction of personal data records.' },
  { heading: 'Security of Personal Data',
    body: 'There needs to be suitable organisational and technical actions and processes in place to ensure the confidentiality, integrity, and availability of both manually and electronically processed personal data. The scope also includes a physical review of hardware and information security protection in place such as VPN, virus and malware protection, password/access controls, physical security of manual paper records and office operating practice.' },
  { heading: 'Subject Access Requests and Individual Rights',
    body: 'There needs to be suitable procedures in place to recognise and respond to individuals’ requests for access to their personal data. For example, SAR log in use, the ability to monitor, redact, and identify exemptions.' },
  { heading: 'Data Sharing',
    body: 'The creation and enforcement of controls/processes, and data sharing agreements, to ensure the sharing of personal data is in line with the principles of all data protection legislation.' },
  { heading: 'Information Risk Assessment (DPIA) and Management',
    body: 'A clear responsibility designated and supported by procedures and policies. A project plan/risk register regularly used across the organisation. DPIAs are reviewed and audited on a frequent basis.' },
  { heading: 'Direct Marketing',
    body: 'Policies and procedures in place around direct marketing, records, and database management to ensure these are followed. Ability to opt in/opt out provided (and the ability to evidence this). Ability to evidence the lawful basis used within direct marketing and training and awareness on this to have been provided across the organisation. The review undertakes a snapshot review of compliance to PECR & the UK GDPR.' },
  { heading: 'Data Breaches',
    body: 'There needs to be in place suitable procedures for recognising a breach, and the actions that should be taken following a breach. For example, a breach log in use, the ability to monitor trends in breaches, and identify any ‘near misses’.' },
  { heading: 'Use of Surveillance Cameras/CCTV',
    body: 'There must be appropriate technical and organisational measures in place for the use of CCTV. This audit looks at whether any specific CCTV DPIAs have been completed, and whether any additional training has been given to individuals who are responsible for these images.' },
];
